/*
 * Conservation nulle (Phase 2) : toutes les données du parcours vivent
 * UNIQUEMENT dans le stockage de session du navigateur (sessionStorage).
 * Fermer l'onglet, terminer volontairement le parcours ou demander
 * l'effacement supprime tout. Rien n'est jamais transmis au serveur.
 *
 * Choix de sécurité assumé (revue de code + audit RGPD aout 2026) :
 * sessionStorage est ici PLUS protecteur que toute alternative serveur,
 * car il garantit que les données de santé ne quittent jamais l'appareil.
 * Le risque XSS résiduel est couvert par des mesures actives :
 * - Content Security Policy stricte sur toutes les réponses (security.py) ;
 * - aucun script tiers ni CDN externe dans l'application ;
 * - aucune injection de HTML brut (pas de dangerouslySetInnerHTML) ;
 * - portée limitée à l'onglet et effacement immédiat via un bouton dédié.
 * Les cookies httpOnly ne conviennent pas : ils transiteraient vers le
 * serveur à chaque requête, ce qui violerait la Conservation Nulle.
 */

import { devWarn } from "@/lib/logger";

const QUIZ_KEY = "cycliq_quiz_v2";
const CONSENT_KEY = "cycliq_consent";
const PRENOM_KEY = "cycliq_prenom";
const JOURNEY_KEY = "cycliq_journey_id";
const SESSION_KEY = "cycliq_session_id";
const SOURCE_KEY = "cycliq_source";

function logStorageIssue(action, key, error) {
  // Journalisation controlee : uniquement l'action, la cle et le type
  // d'erreur. JAMAIS le contenu stocke (conservation nulle).
  devWarn(`[cycliq] stockage session indisponible (${action} ${key}) :`, error?.name || "erreur inconnue");
}

function safeGet(key) {
  try {
    return sessionStorage.getItem(key);
  } catch (error) {
    logStorageIssue("lecture", key, error);
    return null;
  }
}

function safeSet(key, value) {
  try {
    sessionStorage.setItem(key, value);
  } catch (error) {
    logStorageIssue("ecriture", key, error);
  }
}

function safeRemove(key) {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    logStorageIssue("suppression", key, error);
  }
}

/* ----- État du questionnaire (réponses en session uniquement) ----- */

export function saveQuizState(state) {
  safeSet(QUIZ_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
}

export function loadQuizState() {
  const raw = safeGet(QUIZ_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    // Validation stricte de forme : un contenu inattendu (altere ou injecte)
    // est ignore et purge plutot qu'interprete.
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      safeRemove(QUIZ_KEY);
      return null;
    }
    if (parsed.answers !== undefined && (typeof parsed.answers !== "object" || parsed.answers === null || Array.isArray(parsed.answers))) {
      safeRemove(QUIZ_KEY);
      return null;
    }
    return parsed;
  } catch (error) {
    logStorageIssue("analyse", QUIZ_KEY, error);
    safeRemove(QUIZ_KEY);
    return null;
  }
}

export function clearQuizState() {
  safeRemove(QUIZ_KEY);
}

/* ----- Consentement sensible (preuve minimale côté serveur, sans réponses) ----- */

export function setConsent() {
  safeSet(CONSENT_KEY, "1");
}

export function hasConsent() {
  return safeGet(CONSENT_KEY) === "1";
}

/* ----- Prénom : personnalisation locale uniquement ----- */

export function savePrenom(prenom) {
  safeSet(PRENOM_KEY, prenom);
}

export function loadPrenom() {
  return safeGet(PRENOM_KEY) || "";
}

/* ----- Identifiant commercial aléatoire (aucun lien avec les réponses) ----- */

function randomHex(bytes) {
  const array = new Uint8Array(bytes);
  (window.crypto || window.msCrypto).getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function getJourneyId() {
  let jid = safeGet(JOURNEY_KEY);
  if (!jid) {
    jid = `cj${randomHex(16)}`;
    safeSet(JOURNEY_KEY, jid);
  }
  return jid;
}

/* ----- Identifiant anonyme de session (mesure du tunnel) ----- */

export function getSessionId() {
  let sid = safeGet(SESSION_KEY);
  if (!sid) {
    sid = `s${randomHex(10)}`;
    safeSet(SESSION_KEY, sid);
  }
  return sid;
}

/* ----- Source commerciale et paramètres UTM (jamais de santé) ----- */

const UTM_KEY = "cycliq_utm";
const UTM_PARAM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

export function captureSource() {
  try {
    const params = new URLSearchParams(window.location.search);
    const src = params.get("utm_source") || params.get("src") || "";
    if (src && !safeGet(SOURCE_KEY)) safeSet(SOURCE_KEY, src.slice(0, 64));
    // Conservation des UTM complets jusqu'à la création du prospect (spec V2).
    if (!safeGet(UTM_KEY)) {
      const utm = {};
      UTM_PARAM_KEYS.forEach((key) => {
        const value = (params.get(key) || "").slice(0, 100);
        if (value) utm[key] = value;
      });
      if (Object.keys(utm).length > 0) safeSet(UTM_KEY, JSON.stringify(utm));
    }
  } catch (error) {
    logStorageIssue("capture source", SOURCE_KEY, error);
  }
}

export function getSource() {
  return safeGet(SOURCE_KEY) || "";
}

export function getUtmParams() {
  const raw = safeGet(UTM_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const utm = {};
    UTM_PARAM_KEYS.forEach((key) => {
      if (typeof parsed[key] === "string" && parsed[key]) utm[key] = parsed[key].slice(0, 100);
    });
    return utm;
  } catch (error) {
    logStorageIssue("analyse", UTM_KEY, error);
    return {};
  }
}

/* ----- Code personnel du PDF : générateur aléatoire cryptographique ----- */
/* 6 chiffres. Ne quitte JAMAIS le navigateur. */

export function generatePersonalCode() {
  const array = new Uint32Array(1);
  (window.crypto || window.msCrypto).getRandomValues(array);
  const code = array[0] % 1000000;
  return String(code).padStart(6, "0");
}

/* ----- Effacement complet de la session (bouton d'effacement) ----- */

export function clearAllSessionData() {
  [QUIZ_KEY, CONSENT_KEY, PRENOM_KEY, JOURNEY_KEY, SESSION_KEY, SOURCE_KEY, UTM_KEY].forEach(safeRemove);
}
